import { useMutation, useQuery } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/**
 * useSendInvoice — email a single chosen invoice to a customer's invoice
 * recipients (superadmin). Mirrors the statement email send. Also exposes
 * useOpenInvoices() for the "Send invoice" picker.
 */

export interface SendInvoiceResult {
  ok: true;
  customer_id: string;
  qbo_invoice_id: string;
  doc_number: string;
  sent: boolean;
  recipients: string[];
  resend_id: string | null;
  total_amount: number;
  balance: number;
  skipped_reason?: string;
}

export function useSendInvoice() {
  return useMutation<SendInvoiceResult, Error, { customer_id: string; qbo_invoice_id: string }>({
    mutationFn: (input) =>
      adminFetch<SendInvoiceResult>('admin-send-invoice', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}

export interface AdminOpenInvoice {
  qbo_invoice_id: string;
  doc_number: string;
  txn_date: string;
  due_date: string | null;
  total_amount: number;
  balance: number;
}

interface OpenInvoicesResponse {
  ok: true;
  customer_id: string;
  invoices: AdminOpenInvoice[];
}

/**
 * A customer's OPEN invoices for the superadmin "Send invoice" picker. Reads
 * via the admin endpoint (service-role over the ops mirror) since the client
 * invoice view is RLS-scoped to the calling customer.
 */
export function useOpenInvoices(customerId: string | null | undefined) {
  return useQuery<AdminOpenInvoice[]>({
    queryKey: ['admin', 'open-invoices', customerId],
    enabled: !!customerId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const res = await adminFetch<OpenInvoicesResponse>(
        `admin-list-open-invoices?customer_id=${encodeURIComponent(customerId!)}`,
        { method: 'GET' },
      );
      return res.invoices ?? [];
    },
  });
}
